import * as ApolloCommon from '@apollo/client';
import * as Apollo from '@apollo/client/react/components';
import { Delete, Edit, Flip as FlipIcon, Help } from '@mui/icons-material';
import { Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { green, red } from '@mui/material/colors';
import { Theme } from '@mui/material/styles';
import * as Schema from 'generated/graphql/schema';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { compose } from 'recompose';

import { MutationIconButton } from '@/components/buttons/mutation-button';
import { deviceUpdateStopCauseMapping } from '@/graphql/mutations';
import { stopCauseMapping } from '@/graphql/queries';
import { WithDevice, WithIAM, withIAM } from '@/hocs';
import { WithStyles, withStyles } from '@/hocs/with-styles';
import ModifyStopMapping from '@/views/dialogs/sensor-settings/tabs/stop-mapping/modify-stop-mapping';

import { StopCauseMapping } from './stops-mapping-tab';

const styles = (theme: Theme) => ({
  root: {
    width: '100%',
    overflowX: 'hidden',
  },
  widest: {
    width: '50%',
  },
  secondWidest: {
    width: '20%',
  },
  error: {
    color: red[700],
  },
  helpIcon: {
    fontSize: '1em',
    color: 'gray',
    marginBottom: '-2px',
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
});

interface Properties {
  stopMappings: StopCauseMapping[];
  stopCauses: Array<{
    name: string;
    id: string;
  }>;

  refetchQuery: Function;
  peripheralId: string;
}
type ExtendedProps = Properties & WithStyles<typeof styles> & WithTranslation & WithIAM & WithDevice;

interface State {
  deleting: boolean[];
  error: boolean;
  editing: number | null;
}

class MappingTable extends React.PureComponent<ExtendedProps, State> {
  readonly state: State = {
    deleting: [],
    error: false,
    editing: null,
  };

  readonly clear: boolean[];

  constructor(props: ExtendedProps) {
    super(props);

    this.clear = Array.from(props.stopMappings, (map) => false);
    this.state.deleting = this.clear;
  }

  render() {
    const { classes, stopMappings, stopCauses, t, hasPeripheralFeatures, peripheralId } = this.props;

    const [uuid, ...indexParts] = peripheralId.split('-');
    const index = indexParts.join('-');

    const permission = !hasPeripheralFeatures(peripheralId, ['Devices.Configuration'], 'M');

    return (
      <>
        {this.state.error && (
          <Typography variant="caption" className={classes.error}>
            *{t(['shared:emptyFieldsErrorMessage'], { defaultValue: 'Every field needs to be set' })}
          </Typography>
        )}
        <Table size="small" style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.widest}>
                <Tooltip title={t(['device:mappingTagExplanation'], { defaultValue: 'The tag received from the PLC' })}>
                  <Typography variant="body2">
                    {t(['shared:mappingTag'], { defaultValue: 'Mapping Tag' })}
                    <Help className={classes.helpIcon} />
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip
                  title={t(['device:mappingValueExplanation'], {
                    defaultValue: 'The value from a tag mapping to the stop cause',
                  })}
                >
                  <Typography variant="body2">
                    {t(['shared:value'], { defaultValue: 'Value' })}
                    <Help className={classes.helpIcon} />
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.secondWidest}>
                <Tooltip
                  title={t(['device:mappingStopCauseExplanation'], {
                    defaultValue: 'The stop cause for which the mapping should associate with',
                  })}
                >
                  <Typography variant="body2">
                    {t(['shared:stopCause'], { defaultValue: 'Stop cause' })}
                    <Help className={classes.helpIcon} />
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip
                  title={t(['device:mappingPriorityExplanation'], {
                    defaultValue:
                      'The priority determines the order of overruling, so a higher mapping priority will overrule a lower prioritized mapping',
                  })}
                >
                  <Typography variant="body2">
                    {t(['shared:priority'], { defaultValue: 'Priority' })}
                    <Help className={classes.helpIcon} />
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip
                  title={t(['device:lookBackExplanation'], {
                    defaultValue:
                      'Specifies how far back the system should look for matching stops, from the time an event is received. The maximum value is 3600 seconds',
                  })}
                >
                  <Typography variant="body2">
                    {t(['device:lookBack'], { defaultValue: 'Look back' })}
                    <Help className={classes.helpIcon} />
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip
                  title={t(['device:lookForwardExplanation'], {
                    defaultValue:
                      'Specifies how far forward the system should look for matching stops, from the time an event is received. The maximum value is 3600 seconds',
                  })}
                >
                  <Typography variant="body2">
                    {t(['shared:lookForward'], { defaultValue: 'Look forward' })}
                    <Help className={classes.helpIcon} />
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <Apollo.Mutation<Schema.DeviceUpdateStopCauseMappingMutation>
            mutation={deviceUpdateStopCauseMapping}
            onCompleted={this.handleComplete}
            refetchQueries={[{ query: stopCauseMapping, variables: { uuid, index } }]}
            awaitRefetchQueries
          >
            {(updateStopCauseMapping, { loading }) => (
              <TableBody>
                <ModifyStopMapping
                  peripheralId={peripheralId}
                  permission={permission}
                  stopCauses={stopCauses}
                  toMappingInput={this.toMappingInput}
                  updateStopCauseMapping={updateStopCauseMapping}
                  loading={loading && !this.state.deleting.find((load) => load)}
                  setError={this.setError}
                />
                {stopMappings.map((mapping, i) => {
                  if (i === this.state.editing) {
                    return (
                      <ModifyStopMapping
                        mapping={mapping}
                        stopEditing={this.stopEditing}
                        peripheralId={peripheralId}
                        permission={permission}
                        stopCauses={stopCauses}
                        toMappingInput={this.toMappingInput}
                        updateStopCauseMapping={updateStopCauseMapping}
                        loading={loading && !this.state.deleting.find((load) => load)}
                        setError={this.setError}
                      />
                    );
                  }

                  const lookForwardInSeconds = Math.floor(mapping.lookForward / 1000);
                  const lookBackInSeconds = Math.floor(mapping.lookBack / 1000);

                  const priorityTitle = mapping.split
                    ? `${mapping.regPriority} (${t(['stop:splitStop'], { defaultValue: 'Split stop' })})`
                    : mapping.regPriority;

                  return (
                    <TableRow hover onClick={this.editMapping(i)} key={`${mapping.mappingTag}_${mapping.mappingValue}`}>
                      <TableCell component="th" scope="row">
                        <Tooltip title={mapping.mappingTag}>
                          <Typography noWrap variant="body2">
                            {mapping.mappingTag}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={mapping.mappingValue}>
                          <Typography noWrap variant="body2">
                            {mapping.mappingValue}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={mapping.stopCauseName !== undefined ? mapping.stopCauseName : ''}>
                          <Typography noWrap variant="body2">
                            {mapping.stopCauseName}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={priorityTitle}>
                          <Typography style={{ overflow: 'visible' }} noWrap variant="body2">
                            {mapping.regPriority}
                            {mapping.split ? (
                              <FlipIcon style={{ verticalAlign: 'middle', marginLeft: '0.5em' }} fontSize="small" />
                            ) : null}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={lookBackInSeconds}>
                          <Typography noWrap variant="body2">
                            {lookBackInSeconds}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={lookForwardInSeconds}>
                          <Typography noWrap variant="body2">
                            {lookForwardInSeconds}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <MutationIconButton
                          color="primary"
                          loadingColor={green[500]}
                          onClick={this.editMapping(i)}
                          disabled={permission}
                          tooltipPlacement="left"
                          tooltipMsg={t(['shared:edit'], { defaultValue: 'Edit' })}
                          tooltipMsgDisabled={t(['device:noPermissionToChangeConfiguration'], {
                            defaultValue: "You don't have permission to change the sensor configuration",
                          })}
                        >
                          <Edit />
                        </MutationIconButton>
                      </TableCell>
                      <TableCell>
                        <MutationIconButton
                          color="primary"
                          loading={loading && this.state.deleting[i]}
                          loadingColor={red[500]}
                          onClick={this.deleteMapping(updateStopCauseMapping, i)}
                          disabled={permission}
                          tooltipPlacement="right"
                          tooltipMsg={t(['shared:delete'], { defaultValue: 'Delete' })}
                          tooltipMsgDisabled={t(['device:noPermissionToChangeConfiguration'], {
                            defaultValue: "You don't have permission to change the sensor configuration",
                          })}
                        >
                          <Delete />
                        </MutationIconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Apollo.Mutation>
        </Table>
      </>
    );
  }

  private editMapping = (i: number) => () => this.setState({ editing: i });

  private stopEditing = () => this.setState({ editing: null });

  private deleteMapping =
    (updateStopCauseMapping: ApolloCommon.MutationFunction<Schema.DeviceUpdateStopCauseMappingMutation>, i: number) =>
    async () => {
      const stopMap = this.toMappingInput(this.props.stopMappings[i]);
      const deleting = [...this.state.deleting];
      deleting[i] = true;
      this.setState({ deleting });
      await updateStopCauseMapping({
        variables: {
          peripheralId: this.props.peripheralId,
          deleteStopCauseMapping: [stopMap],
          createStopCauseMapping: [],
        },
      });
    };

  private toMappingInput = (mapping: StopCauseMapping): Schema.StopCauseMappingInput => {
    return {
      peripheralIdTarget: this.props.peripheralId,
      peripheralIdEvent: mapping.mappingTag,
      value: mapping.mappingValue,
      stopCauseId: mapping.stopCauseId,
      regPriority: mapping.regPriority,
      lookBack: mapping.lookBack,
      lookForward: mapping.lookForward,
      split: mapping.split || false,
    };
  };

  private setError = (error: boolean) => this.setState({ error });

  private handleComplete = async (data: unknown | null) => {
    if (!data) {
      return;
    }

    await this.props.refetchQuery();
    this.setState({ deleting: this.clear });
  };
}
const enhance = compose<unknown, Properties>(withIAM, withStyles(styles), withTranslation(['shared', 'device']));
export default enhance(MappingTable as React.ComponentType<unknown>);
